/**
 * Card Editor Types — local type definitions matching enterprise-commerce CardTemplate.
 *
 * These types are copied from enterprise-commerce/starters/shopify-algolia/lib/builder/card-template.ts
 * to avoid cross-project imports. They MUST stay in sync with the canonical source.
 */

import type { LucideIcon } from 'lucide-react'
import {
  ImageIcon,
  Type,
  Store,
  Star,
  DollarSign,
  Layers,
  BarChart3,
  ShoppingCart,
} from 'lucide-react'

// ── Types ────────────────────────────────────────────────

export type CardSectionType =
  | 'image'
  | 'title'
  | 'vendor'
  | 'rating'
  | 'price'
  | 'variants'
  | 'volumePrice'
  | 'addToCart'

export interface CardSection {
  type: CardSectionType
  /** data-* attributes from the widget block (e.g., data-image-ratio, data-title-lines) */
  config: Record<string, string>
}

export interface ContainerConfig {
  border: 'none' | 'subtle' | 'rounded'
  hover: 'none' | 'lift'
  shadow: 'none' | 'subtle' | 'medium' | 'strong'
}

export interface CardTemplate {
  sections: CardSection[]
  containerConfig: ContainerConfig
}

// ── Default Template ─────────────────────────────────────

export const DEFAULT_CARD_TEMPLATE: CardTemplate = {
  sections: [
    { type: 'image', config: { 'data-image-ratio': '1:1', 'data-hover-effect': 'zoom' } },
    { type: 'title', config: { 'data-title-lines': '2' } },
    { type: 'vendor', config: {} },
    { type: 'rating', config: {} },
    { type: 'price', config: { 'data-show-compare-price': 'false' } },
  ],
  containerConfig: { border: 'none', hover: 'none', shadow: 'none' },
}

// ── Section Metadata ─────────────────────────────────────

export interface CardSectionMeta {
  label: string
  icon: LucideIcon
  description: string
  hasSettings: boolean
}

export const CARD_SECTION_META: Record<CardSectionType, CardSectionMeta> = {
  image: { label: 'Image', icon: ImageIcon, description: 'Product image', hasSettings: true },
  title: { label: 'Title', icon: Type, description: 'Product name', hasSettings: true },
  vendor: { label: 'Vendor', icon: Store, description: 'Brand / vendor name', hasSettings: false },
  rating: { label: 'Rating', icon: Star, description: 'Star rating', hasSettings: false },
  price: { label: 'Price', icon: DollarSign, description: 'Product price', hasSettings: true },
  variants: { label: 'Variants', icon: Layers, description: 'Color/size swatches', hasSettings: false },
  volumePrice: { label: 'Volume Pricing', icon: BarChart3, description: 'Bulk pricing tiers', hasSettings: false },
  addToCart: { label: 'Add to Cart', icon: ShoppingCart, description: 'Quick-add button', hasSettings: true },
}

/** All section types in default order (for Add Section dropdown) */
export const ALL_SECTION_TYPES: CardSectionType[] = [
  'image', 'title', 'vendor', 'rating', 'price', 'variants', 'volumePrice', 'addToCart',
]

// ── Default configs per section type ─────────────────────

export const DEFAULT_SECTION_CONFIGS: Record<CardSectionType, Record<string, string>> = {
  image: { 'data-image-ratio': '1:1', 'data-hover-effect': 'zoom' },
  title: { 'data-title-lines': '2' },
  vendor: {},
  rating: {},
  price: { 'data-show-compare-price': 'false' },
  variants: {},
  volumePrice: {},
  addToCart: { 'data-button-style': 'filled' },
}

// ── Container config options ─────────────────────────────

export const BORDER_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'subtle', label: 'Subtle' },
  { value: 'rounded', label: 'Rounded' },
]

export const SHADOW_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'subtle', label: 'Subtle' },
  { value: 'medium', label: 'Medium' },
  { value: 'strong', label: 'Strong' },
]

export const HOVER_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'lift', label: 'Lift' },
]

// ── Section-specific setting options ─────────────────────

export const IMAGE_RATIO_OPTIONS = [
  { value: '1:1', label: '1:1' },
  { value: '4:3', label: '4:3' },
  { value: '3:4', label: '3:4' },
  { value: '16:9', label: '16:9' },
]

export const IMAGE_HOVER_OPTIONS = [
  { value: 'zoom', label: 'Zoom' },
  { value: 'none', label: 'None' },
]

export const TITLE_LINES_OPTIONS = [
  { value: '1', label: '1 line' },
  { value: '2', label: '2 lines' },
  { value: '3', label: '3 lines' },
]

export const COMPARE_PRICE_OPTIONS = [
  { value: 'true', label: 'Show' },
  { value: 'false', label: 'Hide' },
]

export const BUTTON_STYLE_OPTIONS = [
  { value: 'filled', label: 'Filled' },
  { value: 'outline', label: 'Outline' },
]
